import { getDB, PendingUpload, isCacheUnavailableError } from './db';
import { apiClient } from '../../api/client';
import { putToPresignedUrl } from '../../api/upload';
import {
  classifyFailure,
  describeFailure,
  isExhausted,
  nextFailureState,
} from './retry-policy';

/** Open the upload queue, or report it as empty when the cache is sealed. */
async function openQueue() {
  try {
    return await getDB();
  } catch (err) {
    if (isCacheUnavailableError(err)) return null;
    throw err;
  }
}

/**
 * Get count of pending uploads waiting to sync.
 */
export async function getPendingUploadCount(): Promise<number> {
  // Read path — zero is the honest count for a queue we cannot reach, and it
  // keeps the "N photos waiting to sync" pill off the screen when there is no
  // sync that could ever drain it. The amber banner covers the real story.
  const db = await openQueue();
  if (!db) return 0;

  const pending = await db.getAllFromIndex('pendingUploads', 'by-status', 'pending');
  const errors = await db.getAllFromIndex('pendingUploads', 'by-status', 'error');
  const retryable = errors.filter((u) => !isExhausted(u.failureKind, u.retryCount));
  return pending.length + retryable.length;
}

/**
 * Photos that ran out of retries. Same contract as `getFailedMutations`: not
 * in the flush, not in the pending count, and NOT gone — a photo that never
 * reached the bucket is the single hardest thing to reconstruct after the fact,
 * so it stays in IndexedDB with its blob and shows in the red bar.
 */
export async function getFailedUploads(): Promise<PendingUpload[]> {
  const db = await openQueue();
  if (!db) return [];

  const failed = await db.getAllFromIndex('pendingUploads', 'by-status', 'failed');
  const errors = await db.getAllFromIndex('pendingUploads', 'by-status', 'error');
  const stranded = errors.filter((u) => isExhausted(u.failureKind, u.retryCount));
  return [...failed, ...stranded];
}

export async function getFailedUploadCount(): Promise<number> {
  return (await getFailedUploads()).length;
}

/** Put exhausted uploads back in the queue at the user's request. */
export async function retryFailedUploads(): Promise<number> {
  const db = await openQueue();
  if (!db) return 0;

  const failed = await getFailedUploads();
  for (const upload of failed) {
    upload.status = 'pending';
    upload.retryCount = 0;
    upload.failureKind = undefined;
    await db.put('pendingUploads', upload);
  }
  return failed.length;
}

/** Discard an exhausted upload for good — only ever from an explicit tap. */
export async function discardFailedUpload(id: string): Promise<void> {
  const db = await openQueue();
  if (!db) return;
  await db.delete('pendingUploads', id);
}

/**
 * Queue a photo/file for offline upload.
 */
export async function queueUpload(upload: PendingUpload): Promise<void> {
  // Write path — intentionally NOT swallowed. This blob is the only copy of
  // the photo the user just took. `PhotoUploader` catches this and shows
  // "Failed to save photo offline."; swallowing it would instead add the photo
  // to the on-screen list as though it were safely stored.
  const db = await getDB();
  await db.put('pendingUploads', upload);
}

/**
 * Get pending uploads for a specific entity (for display).
 */
export async function getPendingUploadsForEntity(entityId: string): Promise<PendingUpload[]> {
  // Read path, feeds a display list — an empty list renders, a rejection
  // would take the surrounding view down with it.
  const db = await openQueue();
  if (!db) return [];

  return db.getAllFromIndex('pendingUploads', 'by-entity', entityId);
}

/**
 * Flush pending uploads to S3 — called during sync cycle after mutations are pushed.
 */
export async function flushPendingUploads(): Promise<{ uploaded: number; failed: number }> {
  // Nothing to flush from a cache we cannot open. Reported as a completed
  // no-op rather than a failure so it does not muddy the sync result.
  const db = await openQueue();
  if (!db) return { uploaded: 0, failed: 0 };

  const pending = await db.getAllFromIndex('pendingUploads', 'by-status', 'pending');
  const errors = await db.getAllFromIndex('pendingUploads', 'by-status', 'error');
  const retryable = errors.filter((u) => !isExhausted(u.failureKind, u.retryCount));
  const toUpload = [...pending, ...retryable];

  let uploaded = 0;
  let failed = 0;

  for (const upload of toUpload) {
    try {
      // Mark as uploading
      upload.status = 'uploading';
      await db.put('pendingUploads', upload);

      // Step 1: Get presigned URL
      const { data: presigned } = await apiClient.post('/uploads/presigned-url', {
        fileName: upload.fileName,
        contentType: upload.contentType,
        entityType: upload.entityType,
        entityId: upload.entityId,
      });

      // Step 2: Upload blob to S3. This site already checked `putRes.ok` —
      // it is routed through the shared helper anyway, because the check was
      // never the only thing missing: there was no timeout either, and a
      // stalled PUT here wedges the whole sync cycle behind one photo.
      await putToPresignedUrl(presigned.uploadUrl, upload.blob, upload.contentType);

      // Step 3: Confirm
      await apiClient.post('/uploads/confirm', {
        key: presigned.key,
        size: upload.blob.size,
      });

      // Mark as done
      upload.status = 'done';
      upload.failureKind = undefined;
      upload.lastError = undefined;
      await db.put('pendingUploads', upload);
      uploaded++;
    } catch (err) {
      console.warn('[upload-sync] Upload failed for', upload.id, err);
      const kind = classifyFailure(err);
      const next = nextFailureState(kind, upload.retryCount);
      upload.status = next.status;
      upload.retryCount = next.retryCount;
      upload.failureKind = kind;
      upload.lastError = describeFailure(err);
      await db.put('pendingUploads', upload);
      failed++;
    }
  }

  // Cleanup completed uploads older than 1 hour. `failed` rows are never swept
  // — the blob is the only copy of that photo.
  const all = await db.getAll('pendingUploads');
  const cutoff = Date.now() - 60 * 60 * 1000;
  for (const u of all) {
    if (u.status === 'done' && new Date(u.createdAt).getTime() < cutoff) {
      await db.delete('pendingUploads', u.id);
    }
  }

  return { uploaded, failed };
}
